import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Upload, Music, Settings as SettingsIcon, Loader2, X, Clock, FileAudio, Edit, LogOut, CreditCard, Info, CheckCircle2, Sparkles, Wand2, Download, Mic2, Scissors, BarChart3, FileText, Coins, ChevronDown, ChevronLeft, ChevronRight, User, Archive, ArchiveRestore, Trash2, Disc3 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import ProcessingModal from '../components/ProcessingModal';
import CreditConfirmModal from '../components/CreditConfirmModal';
import jsmediatags from 'jsmediatags';
import * as Dialog from '@radix-ui/react-dialog';

// All processing phases that indicate the session is still being processed
const PROCESSING_STATUSES = ['uploading', 'processing', 'separating', 'transcribing', 'detecting', 'finalizing'];

export default function Dashboard() {
  const [sessions, setProjects] = useState([]);
  const [archivedSessions, setArchivedSessions] = useState([]);
  const [archivedExpanded, setArchivedExpanded] = useState(false);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [archivedSessionsLoaded, setArchivedSessionsLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingProject, setProcessingProject] = useState(null);
  const [cancelledSessionIds, setCancelledSessionIds] = useState(new Set());
  const [sessionFile, setSessionFile] = useState(null);
  const [sessionMetadata, setSessionMetadata] = useState({
    title: '',
    artist: '',
    qualifiers: [],
    artwork: null
  });
  const [sessionLyrics, setSessionLyrics] = useState(null);
  const [lyricsLoading, setLyricsLoading] = useState(false);
  const [lyricsError, setLyricsError] = useState(null);
  const [musixmatchResults, setMusixmatchResults] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [musixmatchSearching, setMusixmatchSearching] = useState(false);
  const [showManualSearchInput, setShowManualSearchInput] = useState(false);
  const [manualSearchArtist, setManualSearchArtist] = useState('');
  const [manualSearchTitle, setManualSearchTitle] = useState('');
  const [userMinutes, setUserMinutes] = useState(0);
  const [showNoCreditsModal, setShowNoCreditsModal] = useState(false);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [toolsExpanded, setToolsExpanded] = useState(true);
  const [stepsExpanded, setStepsExpanded] = useState(true);
  const [stepsCardHidden, setStepsCardHidden] = useState(() => sessionStorage.getItem('stepsCardHidden') === 'true');
  const [isEnhancingLyrics, setIsEnhancingLyrics] = useState(false);
  const [showUploadCreditModal, setShowUploadCreditModal] = useState(false);
  const [deleteConfirmSession, setDeleteConfirmSession] = useState(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PER_PAGE = 10;
  const { user, logout, userProfile, refreshUser} = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionSetRef = useRef(false); // Track if session has been set to prevent duplicates

  useEffect(() => {
    fetchProjects();
    fetchArchivedSessions();
    fetchUserProfile();
    fetchPackages();
    const interval = setInterval(fetchProjects, 5000); // Poll every 5s for updates
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (searchParams.get('buy') === 'true') {
      setShowNoCreditsModal(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('buy');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (userProfile?.minutes !== undefined) {
      setUserMinutes(userProfile.minutes);
    }
  }, [userProfile]);

  useEffect(() => {
    if (!userProfile?.id || !sessionsLoaded || !archivedSessionsLoaded) return;
    const welcomeKey = `hasSeenWelcome:${userProfile.id}`;
    const hasSeenWelcome = localStorage.getItem(welcomeKey);
    
    if (!hasSeenWelcome && sessions.length === 0 && archivedSessions.length === 0 && userMinutes > 0) {
      setShowWelcomeModal(true);
    }
  }, [userProfile, sessions, archivedSessions, userMinutes, sessionsLoaded, archivedSessionsLoaded]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchUserProfile();
        refreshUser();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshUser]);

  // Helper function to strip parentheses and clean text
  const stripParentheses = (text) => {
    if (!text) return text;
    return text.replace(/\s*\(.*?\)\s*/g, ' ').trim();
  };

  // Search MusixMatch when title and artist are available
  useEffect(() => {
    const searchMusixMatch = async () => {
      if (!sessionFile || !sessionMetadata.title || !sessionMetadata.artist) {
        setMusixmatchResults([]);
        setSelectedTrack(null);
        setSessionLyrics(null);
        setLyricsError(null);
        return;
      }
      
      setMusixmatchSearching(true);
      setLyricsError(null);
      
      try {
        const cleanTitle = stripParentheses(sessionMetadata.title);
        const cleanArtist = stripParentheses(sessionMetadata.artist);
        
        const response = await api.get('/musixmatch/search', {
          params: {
            artist: cleanArtist,
            title: cleanTitle
          }
        });
        
        if (response.data.success && response.data.tracks.length > 0) {
          setMusixmatchResults(response.data.tracks);
          setSelectedTrack(response.data.tracks[0]);
          setLyricsError(null);
        } else {
          setMusixmatchResults([]);
          setSelectedTrack(null);
          setLyricsError('No matching songs found');
        }
      } catch (error) {
        console.error('Failed to search MusixMatch:', error);
        setMusixmatchResults([]);
        setSelectedTrack(null);
        setLyricsError('Failed to search for lyrics');
      } finally {
        setMusixmatchSearching(false);
      }
    };
    
    const timeoutId = setTimeout(searchMusixMatch, 500);
    return () => clearTimeout(timeoutId);
  }, [sessionFile, sessionMetadata.title, sessionMetadata.artist]);

  const handleSearchAgain = () => {
    setShowManualSearchInput(true);
    setManualSearchArtist(sessionMetadata.artist);
    setManualSearchTitle(sessionMetadata.title);
    setSelectedTrack(null);
    setMusixmatchResults([]);
    setLyricsError(null);
  };

  const handleManualSearch = async () => {
    if (!manualSearchArtist || !manualSearchTitle) {
      toast.error('Please enter both artist and title');
      return;
    }

    setMusixmatchSearching(true);
    setLyricsError(null);

    try {
      const cleanTitle = stripParentheses(manualSearchTitle);
      const cleanArtist = stripParentheses(manualSearchArtist);

      const response = await api.get('/musixmatch/search', {
        params: {
          artist: cleanArtist,
          title: cleanTitle
        }
      });

      if (response.data.success && response.data.tracks.length > 0) {
        setMusixmatchResults(response.data.tracks);
        setSelectedTrack(response.data.tracks[0]);
      } else {
        setMusixmatchResults([]);
        setSelectedTrack(null);
        setLyricsError('No matching songs found');
      }
    } catch (error) {
      console.error('Failed to search MusixMatch:', error);
      setLyricsError('Failed to search for lyrics');
    } finally {
      setMusixmatchSearching(false);
    }
  };

  const handleConfirmTrack = (track) => {
    setSelectedTrack(track);
    setShowManualSearchInput(false);
  };

  useEffect(() => {
    const toolsTimer = setTimeout(() => {
      setToolsExpanded(false);
    }, 3000);
    const stepsTimer = setTimeout(() => {
      setStepsExpanded(false);
    }, 6000);
    return () => {
      clearTimeout(toolsTimer);
      clearTimeout(stepsTimer);
    };
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await api.get('/user/profile');
      setUserMinutes(response.data.minutes || 0);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await api.get('/payments/packages');
      setPackages(response.data.packages || []);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const purchaseMinutes = async (minutes) => {
    try {
      setSelectedPackage(minutes);
      setIsProcessingPayment(true);
      
      const originUrl = window.location.origin;
      const response = await api.post('/payments/checkout/session', {
        minutes,
        origin_url: originUrl
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setIsProcessingPayment(false);
      setSelectedPackage(null);
    }
  };

  // Watch for processing sessions and show modal
  useEffect(() => {
    const activeProject = sessions.find(p => 
      PROCESSING_STATUSES.includes(p.status) && !cancelledSessionIds.has(p.id)
    );
    
    if (activeProject && !processingProject) {
      setProcessingProject(activeProject);
    } else if (activeProject && processingProject) {
      // Update status if changed
      if (activeProject.status !== processingProject.status) {
        setProcessingProject(activeProject);
      }
    } else if (!activeProject && processingProject) {
      // Processing complete
      const completedProject = sessions.find(p => p.id === processingProject.id);
      if (completedProject?.status === 'complete') {
        if (completedProject.no_curses_detected) {
          toast.info('Processing complete! No explicit content was detected in this track.');
        } else {
          toast.success('Processing complete! Your track is ready to edit.');
        }
        setTimeout(async () => {
          // Auto-enhance lyrics if available
          if (completedProject.source_lyrics) {
            setIsEnhancingLyrics(true);
            try {
              await api.post(`/sessions/${completedProject.id}/enhance-lyrics`);
            } catch (err) {
              console.log('Auto-enhance lyrics skipped:', err.response?.data?.detail || err.message);
            }
            setIsEnhancingLyrics(false);
          }
          setProcessingProject(null);
          navigate(`/editor/${completedProject.id}`);
        }, 1500);
      } else if (completedProject?.status === 'error') {
        toast.error(completedProject.error_message || 'Processing failed. Please try again.');
        setProcessingProject(null);
      }
    }
  }, [sessions, processingProject, navigate, cancelledSessionIds]);

  // Cleanup cancelled session IDs once they transition to non-processing state
  useEffect(() => {
    if (cancelledSessionIds.size === 0) return;
    
    const idsToRemove = [];
    cancelledSessionIds.forEach(cancelledId => {
      const session = sessions.find(s => s.id === cancelledId);
      // If session no longer exists or is no longer in processing state, remove from cancelled set
      if (!session || !PROCESSING_STATUSES.includes(session.status)) {
        idsToRemove.push(cancelledId);
      }
    });
    
    if (idsToRemove.length > 0) {
      setCancelledSessionIds(prev => {
        const next = new Set(prev);
        idsToRemove.forEach(id => next.delete(id));
        return next;
      });
    }
  }, [sessions, cancelledSessionIds]);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/sessions?limit=100');
      const sessionsData = response.data.sessions || response.data;
      setProjects(Array.isArray(sessionsData) ? sessionsData : []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setSessionsLoaded(true);
    }
  };

  const fetchArchivedSessions = async () => {
    try {
      const response = await api.get('/sessions/archived');
      setArchivedSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch archived sessions:', error);
    } finally {
      setArchivedSessionsLoaded(true);
    }
  };

  const archiveSession = async (sessionId) => {
    try {
      await api.put(`/sessions/${sessionId}/archive`);
      toast.success('Session archived');
      fetchProjects();
      fetchArchivedSessions();
    } catch (error) {
      toast.error('Failed to archive session');
      console.error('Failed to archive session:', error);
    }
  };

  const restoreSession = async (sessionId) => {
    try {
      await api.put(`/sessions/${sessionId}/restore`);
      toast.success('Session restored');
      fetchProjects();
      fetchArchivedSessions();
    } catch (error) {
      toast.error('Failed to restore session');
      console.error('Failed to restore session:', error);
    }
  };

  const deleteSession = async (sessionId) => {
    try {
      await api.delete(`/sessions/${sessionId}`);
      toast.success('Session deleted permanently');
      setDeleteConfirmSession(null);
      fetchArchivedSessions();
    } catch (error) {
      toast.error('Failed to delete session');
      console.error('Failed to delete session:', error);
    }
  };

  // Get current sessions (complete but not yet processed/exported)
  const currentSessions = sessions.filter(s => 
    s.status === 'complete' && !s.files?.clean_url
  );

  // Get history sessions (processed and exported)
  const allHistorySessions = sessions.filter(s =>
    s.files?.clean_url  // Has been processed and exported
  );
  const totalHistoryPages = Math.ceil(allHistorySessions.length / HISTORY_PER_PAGE);
  const historySessions = allHistorySessions.slice(
    (historyPage - 1) * HISTORY_PER_PAGE,
    historyPage * HISTORY_PER_PAGE
  );

  const handleHistoryPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalHistoryPages) {
      setHistoryPage(newPage);
    }
  };

  const handleEditSession = (session) => {
    navigate(`/editor/${session.id}`);
  };

  const formatTime = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const cleanTitle = (title) => {
    if (!title) return title;
    return title.replace(/\s*\(Clean\)\s*$/i, '').trim();
  };

  // Helper function to parse filename intelligently
  const parseFilename = (filename) => {
    // Remove extension
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
    
    // Extract qualifiers (Intro, Edit, Remix, etc.) in parentheses
    const qualifiersRegex = /\(([^)]+)\)/g;
    const qualifiers = [];
    let match;
    let baseName = nameWithoutExt;
    
    while ((match = qualifiersRegex.exec(nameWithoutExt)) !== null) {
      qualifiers.push(match[1]);
      baseName = baseName.replace(match[0], '').trim();
    }
    
    // Try to split by common delimiters
    let artist = '';
    let title = baseName;
    
    // Check for " - " delimiter (most common)
    if (baseName.includes(' - ')) {
      const parts = baseName.split(' - ');
      artist = parts[0].trim();
      title = parts.slice(1).join(' - ').trim();
    }
    // Check for " – " (em dash)
    else if (baseName.includes(' – ')) {
      const parts = baseName.split(' – ');
      artist = parts[0].trim();
      title = parts.slice(1).join(' – ').trim();
    }
    
    return {
      artist,
      title: title || baseName,
      qualifiers
    };
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Reset the ref for new file drop
    sessionSetRef.current = false;

    // Get audio duration
    const audio = new Audio();
    const objectUrl = URL.createObjectURL(file);
    audio.src = objectUrl;
    
    // Try to read ID3 tags
    jsmediatags.read(file, {
      onSuccess: (tag) => {
        const tags = tag.tags;
        const parsed = parseFilename(file.name);
        
        // Extract artwork from ID3 tags
        let artworkUrl = null;
        if (tags.picture) {
          const { data, format } = tags.picture;
          const base64String = data.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
          artworkUrl = `data:${format};base64,${btoa(base64String)}`;
        }
        
        // Set session file with duration
        const handleLoadedMetadata = () => {
          // Prevent duplicate calls
          if (sessionSetRef.current) return;
          sessionSetRef.current = true;
          
          // Get file extension without dot
          const ext = file.name.split('.').pop().toUpperCase();
          
          setSessionFile({
            file,
            name: file.name,
            size: file.size,
            type: file.type,
            format: ext, // MP3, WAV, FLAC, etc.
            duration: audio.duration || 0
          });
          
          // Use ID3 tags if available, otherwise use parsed filename
          setSessionMetadata({
            title: tags.title || parsed.title,
            artist: tags.artist || parsed.artist,
            qualifiers: parsed.qualifiers,
            artwork: artworkUrl
          });
          
          URL.revokeObjectURL(objectUrl);
        };
        
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        
        // Also try to load immediately if already cached
        if (audio.readyState >= 1) {
          handleLoadedMetadata();
        }
        
        // Fallback if duration doesn't load within 3 seconds
        setTimeout(() => {
          if (!sessionSetRef.current) {
            sessionSetRef.current = true;
            const ext = file.name.split('.').pop().toUpperCase();
            
            setSessionFile({
              file,
              name: file.name,
              size: file.size,
              type: file.type,
              format: ext,
              duration: audio.duration || 0
            });
            setSessionMetadata({
              title: tags.title || parsed.title,
              artist: tags.artist || parsed.artist,
              qualifiers: parsed.qualifiers,
              artwork: artworkUrl
            });
            URL.revokeObjectURL(objectUrl);
          }
        }, 3000);
      },
      onError: (error) => {
        // Fallback to filename parsing if ID3 reading fails
        console.log('ID3 tag reading failed, using filename parsing:', error);
        const parsed = parseFilename(file.name);
        
        const handleLoadedMetadata = () => {
          // Prevent duplicate calls
          if (sessionSetRef.current) return;
          sessionSetRef.current = true;
          
          const ext = file.name.split('.').pop().toUpperCase();
          
          setSessionFile({
            file,
            name: file.name,
            size: file.size,
            type: file.type,
            format: ext,
            duration: audio.duration || 0
          });
          setSessionMetadata({
            title: parsed.title,
            artist: parsed.artist,
            qualifiers: parsed.qualifiers,
            artwork: null
          });
          URL.revokeObjectURL(objectUrl);
        };
        
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        
        if (audio.readyState >= 1) {
          handleLoadedMetadata();
        }
        
        setTimeout(() => {
          if (!sessionSetRef.current) {
            sessionSetRef.current = true;
            const ext = file.name.split('.').pop().toUpperCase();
            
            setSessionFile({
              file,
              name: file.name,
              size: file.size,
              type: file.type,
              format: ext,
              duration: 0
            });
            setSessionMetadata({
              title: parsed.title,
              artist: parsed.artist,
              qualifiers: parsed.qualifiers,
              artwork: null
            });
            URL.revokeObjectURL(objectUrl);
          }
        }, 3000);
      }
    });
  }, [sessionFile]);

  const handleUploadAndAnalyze = () => {
    if (!sessionFile) return;
    setShowUploadCreditModal(true);
  };

  const handleUploadConfirmed = async () => {
    if (!sessionFile) return;
    
    setShowUploadCreditModal(false);
    setUploading(true);
    setUploadProgress(0);

    try {
      // Step 0: Fetch lyrics from MusixMatch if a track is selected
      let lyricsToUpload = null;
      if (selectedTrack?.commontrack_id) {
        setLyricsLoading(true);
        try {
          const lyricsResponse = await api.get(`/musixmatch/lyrics/${selectedTrack.commontrack_id}`);
          if (lyricsResponse.data.success) {
            lyricsToUpload = lyricsResponse.data.lyrics;
          }
        } catch (lyricsError) {
          console.error('Failed to fetch lyrics:', lyricsError);
        } finally {
          setLyricsLoading(false);
        }
      }

      const formData = new FormData();
      formData.append('file', sessionFile.file);
      if (sessionMetadata.title) formData.append('title', sessionMetadata.title);
      if (sessionMetadata.artist) formData.append('artist', sessionMetadata.artist);
      if (sessionMetadata.qualifiers && sessionMetadata.qualifiers.length > 0) {
        formData.append('qualifiers', JSON.stringify(sessionMetadata.qualifiers));
      }
      if (lyricsToUpload) {
        formData.append('source_lyrics', lyricsToUpload);
      }

      // Step 1: Upload file
      const uploadResponse = await api.post('/sessions/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      const sessionId = uploadResponse.data.id;
      toast.success('Upload successful! Starting analysis...');
      
      // Step 2: Start analysis
      const analyzeResponse = await api.post(`/sessions/${sessionId}/analyze`);
      
      toast.success('Analysis started! Processing your track.');
      
      // Clear session and set processing session
      setSessionFile(null);
      setSessionMetadata({ title: '', artist: '', qualifiers: [], artwork: null });
      setMusixmatchResults([]);
      setSelectedTrack(null);
      setShowManualSearchInput(false);
      setManualSearchArtist('');
      setManualSearchTitle('');
      setProcessingProject(uploadResponse.data);
      
      fetchProjects();
      fetchUserProfile();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCancelSession = () => {
    setSessionFile(null);
    setSessionMetadata({ title: '', artist: '', qualifiers: [], artwork: null });
    setMusixmatchResults([]);
    setSelectedTrack(null);
    setShowManualSearchInput(false);
    setManualSearchArtist('');
    setManualSearchTitle('');
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.flac', '.aiff'],
    },
    maxFiles: 1,
    disabled: uploading || !!sessionFile,
  });

  const getStatusBadge = (status) => {
    const variants = {
      uploading: 'default',
      processing: 'secondary',
      separating: 'secondary',
      transcribing: 'secondary',
      detecting: 'secondary',
      finalizing: 'secondary',
      complete: 'default',
      error: 'destructive',
    };

    const colors = {
      uploading: 'bg-blue-500/10 text-blue-500',
      processing: 'bg-yellow-500/10 text-yellow-500',
      separating: 'bg-orange-500/10 text-orange-500',
      transcribing: 'bg-purple-500/10 text-purple-500',
      detecting: 'bg-cyan-500/10 text-cyan-500',
      finalizing: 'bg-indigo-500/10 text-indigo-500',
      complete: 'bg-green-500/10 text-green-500',
      error: 'bg-red-500/10 text-red-500',
    };

    const isProcessing = PROCESSING_STATUSES.includes(status);

    return (
      <Badge className={colors[status] || colors.processing}>
        {isProcessing ? (
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
        ) : null}
        {status}
      </Badge>
    );
  };

  const handleProjectClick = (session) => {
    if (session.status === 'complete') {
      navigate(`/editor/${session.id}`);
    }
  };

  return (
    <div className="flex-1">
      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* What You Can Do Section - Always visible */}
        {!sessionFile && !stepsCardHidden && (
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setStepsCardHidden(true);
                sessionStorage.setItem('stepsCardHidden', 'true');
              }}
              className="absolute top-4 right-4 p-1 rounded-md hover:bg-background/50 transition-colors z-10"
              title="Dismiss"
            >
              <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
            </button>
            <CardHeader 
              className="cursor-pointer group pr-10"
              onClick={() => setStepsExpanded(!stepsExpanded)}
            >
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Clean Your Songs in 3 Easy Steps
                <ChevronDown className={`h-4 w-4 ml-auto transition-transform duration-300 ${stepsExpanded ? 'rotate-180' : ''}`} />
              </CardTitle>
              <CardDescription className="text-base group-hover:text-foreground/70 transition-colors">
                Remove explicit content from any song - fast, accurate, and radio-ready
              </CardDescription>
            </CardHeader>
            <div 
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                stepsExpanded 
                  ? 'max-h-[1000px] opacity-100' 
                  : 'max-h-0 opacity-0'
              }`}
            >
            <CardContent className="space-y-6">
              {/* Main Cleaning Process - Step by Step */}
              <div className="space-y-4">
                {/* Step 1 */}
                <div className="flex items-start gap-4 p-4 rounded-lg bg-background/50 border border-border/50">
                  <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0 text-primary-foreground font-bold text-lg">
                    1
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
                      <Upload className="h-4 w-4 text-primary" />
                      Upload Your Song
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Drag and drop any audio file (MP3, WAV, M4A, and more). We support files up to 2GB.
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex items-start gap-4 p-4 rounded-lg bg-background/50 border border-border/50">
                  <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0 text-primary-foreground font-bold text-lg">
                    2
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
                      <Wand2 className="h-4 w-4 text-primary" />
                      AI Detects & Removes Curse Words
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Our AI separates vocals from the music, transcribes the lyrics, identifies explicit words, and silences them automatically. The whole process takes just a few minutes.
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex items-start gap-4 p-4 rounded-lg bg-background/50 border border-border/50">
                  <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0 text-primary-foreground font-bold text-lg">
                    3
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
                      <Download className="h-4 w-4 text-primary" />
                      Download Your Clean Version
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Preview the cleaned audio, make any manual adjustments if needed, and export your radio-ready track. Perfect for radio, streaming, events, or family listening.
                    </p>
                  </div>
                </div>
              </div>

              {/* Collapsible Tools Section */}
              <div 
                className="flex items-center gap-4 cursor-pointer group"
                onClick={() => setToolsExpanded(!toolsExpanded)}
              >
                <div className="flex-1 h-px bg-border"></div>
                <span className="text-sm text-muted-foreground font-medium flex items-center gap-2 group-hover:text-foreground transition-colors">
                  Plus More Powerful Tools
                  <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${toolsExpanded ? 'rotate-180' : ''}`} />
                </span>
                <div className="flex-1 h-px bg-border"></div>
              </div>

              {/* Other Features Grid - Animated */}
              <div 
                className={`grid sm:grid-cols-3 gap-3 overflow-hidden transition-all duration-500 ease-in-out ${
                  toolsExpanded 
                    ? 'max-h-40 opacity-100' 
                    : 'max-h-0 opacity-0'
                }`}
              >
                <div className="flex items-center gap-3 p-3 rounded-lg bg-background/30 border border-border/30 hover:border-primary/30 transition-colors">
                  <Scissors className="h-5 w-5 text-orange-500 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-sm">Stem Separation</h4>
                    <p className="text-xs text-muted-foreground">Extract drums, bass, guitar & more</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 rounded-lg bg-background/30 border border-border/30 hover:border-primary/30 transition-colors">
                  <BarChart3 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-sm">Track Analytics</h4>
                    <p className="text-xs text-muted-foreground">BPM, key, genre & structure</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 rounded-lg bg-background/30 border border-border/30 hover:border-primary/30 transition-colors">
                  <Music className="h-5 w-5 text-purple-500 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-sm">Audio to MIDI</h4>
                    <p className="text-xs text-muted-foreground">Convert any track to MIDI</p>
                  </div>
                </div>
              </div>

              {/* Minutes Info */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-primary/5 border border-primary/20">
                <div className="flex items-center gap-3">
                  <Coins className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm font-medium">Pay only for what you use</p>
                    <p className="text-xs text-muted-foreground">1 minute of audio = 1 minute charged</p>
                  </div>
                </div>
                {userMinutes > 0 && (
                  <div className="text-right">
                    <p className="text-lg font-bold text-primary">{userMinutes.toFixed(1)} min</p>
                    <p className="text-xs text-muted-foreground">available</p>
                  </div>
                )}
              </div>
            </CardContent>
            </div>
          </Card>
        )}

        {!sessionFile ? (
          /* Upload Section */
          <Card data-testid="upload-section">
            <CardHeader>
              <CardTitle>Upload Audio File</CardTitle>
              <CardDescription>
                Upload a song or mix to automatically detect and censor curse words
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                {...getRootProps()}
                className={`group relative flex h-56 items-center justify-center rounded-lg border-2 border-dashed transition-colors cursor-pointer ${
                  isDragActive
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="upload-dropzone"
              >
                <input {...getInputProps()} />
                <div className="text-center space-y-4">
                  <div className="mx-auto h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Upload className="h-6 w-6 text-primary" />
                  </div>
                  {uploading ? (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Uploading...</p>
                      <Progress value={uploadProgress} className="w-64 mx-auto" data-testid="upload-progress" />
                      <p className="text-xs text-muted-foreground">{uploadProgress}%</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm font-medium">
                        {isDragActive ? 'Drop your audio file here' : 'Drag & drop audio or click to browse'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Supported: MP3, WAV, FLAC, AAC, OGG, M4A, AIFF
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Session Window */
          <Card data-testid="session-window">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Session Preview</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelSession}
                  data-testid="cancel-session-button"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                Review and edit track details before uploading
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* File Info with Artwork */}
              <div className="flex items-start space-x-4 p-4 bg-secondary/30 rounded-lg">
                {/* Artwork or Icon */}
                {sessionMetadata.artwork ? (
                  <img
                    src={sessionMetadata.artwork}
                    alt="Album artwork"
                    className="h-16 w-16 rounded-lg object-cover flex-shrink-0"
                    data-testid="session-artwork"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <FileAudio className="h-8 w-8 text-primary" />
                  </div>
                )}
                
                {/* File Details */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{sessionFile.name}</p>
                  <div className="flex items-center space-x-4 mt-2 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Clock className="h-3 w-3" />
                      <span>
                        {sessionFile.duration > 0 
                          ? `${Math.floor(sessionFile.duration / 60)}:${String(Math.floor(sessionFile.duration % 60)).padStart(2, '0')}`
                          : 'Loading...'}
                      </span>
                    </div>
                    <span>{(sessionFile.size / (1024 * 1024)).toFixed(2)} MB</span>
                    <span className="uppercase font-medium">{sessionFile.format || 'AUDIO'}</span>
                  </div>
                </div>
              </div>

              {/* Metadata Form */}
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Track Title *</Label>
                  <Input
                    id="title"
                    value={sessionMetadata.title}
                    onChange={(e) => setSessionMetadata(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter track title"
                    data-testid="session-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="artist">Artist</Label>
                  <Input
                    id="artist"
                    value={sessionMetadata.artist}
                    onChange={(e) => setSessionMetadata(prev => ({ ...prev, artist: e.target.value }))}
                    placeholder="Enter artist name (optional)"
                    data-testid="session-artist-input"
                  />
                </div>
                
                {/* Song Matching */}
                {sessionMetadata.title && sessionMetadata.artist && (
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Music className="h-4 w-4" />
                      Lyrics Source
                    </Label>
                    
                    {musixmatchSearching ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-secondary/30 rounded-lg">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Searching for matching song...
                      </div>
                    ) : selectedTrack && musixmatchResults.length <= 1 ? (
                      <div className="p-4 bg-secondary/30 rounded-lg border border-green-500/30">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                              <span className="text-sm font-medium">Song Found</span>
                            </div>
                            <p className="mt-1 font-semibold">{selectedTrack.track_name}</p>
                            <p className="text-sm text-muted-foreground">by {selectedTrack.artist_name}</p>
                            {selectedTrack.album_name && (
                              <p className="text-xs text-muted-foreground mt-1">Album: {selectedTrack.album_name}</p>
                            )}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSearchAgain}
                          >
                            Search Again
                          </Button>
                        </div>
                      </div>
                    ) : showManualSearchInput ? (
                      <div className="p-4 bg-secondary/30 rounded-lg space-y-3">
                        <div className="text-sm font-medium">Search for the correct song:</div>
                        <div className="grid gap-2">
                          <Input
                            placeholder="Artist name"
                            value={manualSearchArtist}
                            onChange={(e) => setManualSearchArtist(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
                          />
                          <Input
                            placeholder="Song title"
                            value={manualSearchTitle}
                            onChange={(e) => setManualSearchTitle(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
                          />
                          <Button
                            onClick={handleManualSearch}
                            disabled={musixmatchSearching}
                            size="sm"
                          >
                            {musixmatchSearching ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Searching...
                              </>
                            ) : (
                              'Search'
                            )}
                          </Button>
                        </div>
                        
                        {musixmatchResults.length > 0 && (
                          <div className="space-y-2 mt-3 pt-3 border-t">
                            <div className="text-sm text-muted-foreground">Select the correct song:</div>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {musixmatchResults.map((track) => (
                                <div
                                  key={track.commontrack_id}
                                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                                    selectedTrack?.commontrack_id === track.commontrack_id
                                      ? 'bg-primary/20 border border-primary'
                                      : 'bg-background hover:bg-secondary/50 border border-transparent'
                                  }`}
                                  onClick={() => handleConfirmTrack(track)}
                                >
                                  <p className="font-medium">{track.track_name}</p>
                                  <p className="text-sm text-muted-foreground">by {track.artist_name}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {lyricsError && (
                          <div className="text-sm text-muted-foreground">{lyricsError}</div>
                        )}
                      </div>
                    ) : musixmatchResults.length > 1 ? (
                      <div className="p-4 bg-secondary/30 rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium">Multiple matches found. Select the correct song:</div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSearchAgain}
                          >
                            Search Again
                          </Button>
                        </div>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {musixmatchResults.map((track) => (
                            <div
                              key={track.commontrack_id}
                              className={`p-3 rounded-lg cursor-pointer transition-colors ${
                                selectedTrack?.commontrack_id === track.commontrack_id
                                  ? 'bg-primary/20 border border-primary'
                                  : 'bg-background hover:bg-secondary/50 border border-transparent'
                              }`}
                              onClick={() => handleConfirmTrack(track)}
                            >
                              <p className="font-medium">{track.track_name}</p>
                              <p className="text-sm text-muted-foreground">by {track.artist_name}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : lyricsError ? (
                      <div className="p-3 bg-secondary/30 rounded-lg text-sm text-muted-foreground flex items-center justify-between">
                        <span>{lyricsError}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSearchAgain}
                        >
                          Try Again
                        </Button>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  onClick={handleUploadAndAnalyze}
                  disabled={!sessionMetadata.title || uploading}
                  size="lg"
                  data-testid="upload-analyze-button"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload & Analyze
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Sessions */}
        {!sessionFile && currentSessions.length > 0 && (
          <Card data-testid="current-sessions-section">
            <CardHeader>
              <CardTitle>Current Sessions</CardTitle>
              <CardDescription>
                Sessions that have been analyzed and are ready for editing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Artist</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Curses Detected</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentSessions.map((session) => (
                    <TableRow 
                      key={session.id}
                      data-testid={`current-session-row-${session.id}`}
                    >
                      <TableCell className="font-medium">
                        {cleanTitle(session.title || session.filename)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {session.artist || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatTime(session.duration)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <Badge variant={session.regions?.length > 0 ? 'destructive' : 'secondary'}>
                          {session.regions?.length || 0}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(session.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleEditSession(session)}
                            data-testid={`edit-session-${session.id}`}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit & Export
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => archiveSession(session.id)}
                            data-testid={`archive-current-session-${session.id}`}
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Sessions History */}
        {!sessionFile && allHistorySessions.length > 0 && (
          <Card data-testid="sessions-history-section">
            <CardHeader>
              <CardTitle>Sessions</CardTitle>
              <CardDescription>
                Previously processed and exported sessions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Artist</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Curses Detected</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historySessions.map((session) => (
                    <TableRow 
                      key={session.id}
                      data-testid={`history-session-row-${session.id}`}
                    >
                      <TableCell className="font-medium">
                        {cleanTitle(session.title || session.filename)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {session.artist || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatTime(session.duration)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <Badge variant={session.regions?.length > 0 ? 'destructive' : 'secondary'}>
                          {session.regions?.length || 0}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(session.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditSession(session)}
                            data-testid={`view-session-${session.id}`}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            View & Re-export
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => archiveSession(session.id)}
                            data-testid={`archive-session-${session.id}`}
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Pagination Controls */}
              {totalHistoryPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {((historyPage - 1) * HISTORY_PER_PAGE) + 1} - {Math.min(historyPage * HISTORY_PER_PAGE, allHistorySessions.length)} of {allHistorySessions.length} sessions
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleHistoryPageChange(historyPage - 1)}
                      disabled={historyPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalHistoryPages }, (_, i) => i + 1)
                        .filter(page => {
                          if (totalHistoryPages <= 5) return true;
                          if (page === 1 || page === totalHistoryPages) return true;
                          if (Math.abs(page - historyPage) <= 1) return true;
                          return false;
                        })
                        .map((page, index, arr) => (
                          <React.Fragment key={page}>
                            {index > 0 && arr[index - 1] !== page - 1 && (
                              <span className="px-1 text-muted-foreground">...</span>
                            )}
                            <Button
                              variant={historyPage === page ? "default" : "outline"}
                              size="sm"
                              className="w-8 h-8 p-0"
                              onClick={() => handleHistoryPageChange(page)}
                            >
                              {page}
                            </Button>
                          </React.Fragment>
                        ))
                      }
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleHistoryPageChange(historyPage + 1)}
                      disabled={historyPage === totalHistoryPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Archived Sessions */}
        {!sessionFile && archivedSessions.length > 0 && (
          <Card data-testid="archived-sessions-section">
            <CardHeader
              onClick={() => setArchivedExpanded(!archivedExpanded)}
              className="cursor-pointer hover:bg-secondary/20 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Archive className="h-5 w-5" />
                    Archived Sessions
                    <Badge variant="outline">{archivedSessions.length}</Badge>
                  </CardTitle>
                  <CardDescription>
                    Sessions that have been archived
                  </CardDescription>
                </div>
                {archivedExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronDown className="h-5 w-5 transform -rotate-90" />}
              </div>
            </CardHeader>
            {archivedExpanded && (
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Artist</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedSessions.map((session) => (
                      <TableRow 
                        key={session.id}
                        data-testid={`archived-session-row-${session.id}`}
                      >
                        <TableCell className="font-medium text-muted-foreground">
                          {cleanTitle(session.title || session.filename)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {session.artist || '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatTime(session.duration)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(session.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => restoreSession(session.id)}
                              data-testid={`restore-session-${session.id}`}
                            >
                              <ArchiveRestore className="h-4 w-4 mr-2" />
                              Restore
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeleteConfirmSession(session)}
                              data-testid={`delete-session-${session.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            )}
          </Card>
        )}
      </div>

      {/* Processing Modal */}
      <ProcessingModal 
        isOpen={!!processingProject}
        status={processingProject?.status || 'uploading'}
        sessionId={processingProject?.id}
        onClose={() => {}}
        onCancel={(sessionId) => {
          // Add to cancelled set to prevent modal from reopening
          setCancelledSessionIds(prev => new Set([...prev, sessionId]));
          setProcessingProject(null);
          toast.info('Processing cancelled. You can try uploading again.');
          loadSessions();
        }}
        showEnhancing={!!processingProject?.source_lyrics}
        isEnhancing={isEnhancingLyrics}
      />

      {/* Upload Credit Confirmation Modal */}
      <CreditConfirmModal
        isOpen={showUploadCreditModal}
        onClose={() => setShowUploadCreditModal(false)}
        onConfirm={handleUploadConfirmed}
        operationName="Upload & Clean"
        operationCost={Math.ceil((sessionFile?.duration || 0) / 60)}
        currentCredits={userMinutes}
        icon={Wand2}
      />

      {/* No Credits Modal */}
      <Dialog.Root open={showNoCreditsModal} onOpenChange={setShowNoCreditsModal}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-xl shadow-xl z-50 w-full max-w-md p-6">
            <div className="text-center mb-6">
              <div className="mx-auto h-16 w-16 rounded-full bg-orange-500/10 flex items-center justify-center mb-4">
                <CreditCard className="h-8 w-8 text-orange-500" />
              </div>
              <Dialog.Title className="text-xl font-bold mb-2">
                Not Enough Minutes
              </Dialog.Title>
              <Dialog.Description className="text-muted-foreground">
                {sessionFile && (
                  <>
                    This song is <span className="font-semibold text-foreground">{Math.ceil((sessionFile.duration || 0) / 60)} minutes</span> long and requires {Math.ceil((sessionFile.duration || 0) / 60)} minutes.
                    <br />
                    You currently have <span className="font-semibold text-foreground">{userMinutes.toFixed(1)} minutes</span> available.
                  </>
                )}
              </Dialog.Description>
            </div>

            <div className="bg-secondary/30 rounded-lg p-4 mb-6">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Info className="h-4 w-4 text-primary" />
                How Pricing Works
              </h4>
              <p className="text-sm text-muted-foreground">
                Minutes are deducted based on song length. A 3-minute song uses 3 minutes, a 5-minute song uses 5 minutes, and so on. This ensures you only pay for what you use.
              </p>
            </div>

            {packages.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold mb-3">Available Packages</h4>
                <div className="grid grid-cols-3 gap-2">
                  {packages.slice(0, 3).map((pkg) => (
                    <button
                      key={pkg.minutes}
                      onClick={() => !isProcessingPayment && setSelectedPackage(pkg.minutes)}
                      disabled={isProcessingPayment}
                      className={`text-center p-3 rounded-lg border transition-all cursor-pointer ${
                        selectedPackage === pkg.minutes
                          ? 'bg-primary/20 border-primary ring-2 ring-primary/50'
                          : 'bg-secondary/30 border-border hover:border-primary/50 hover:bg-secondary/50'
                      } ${isProcessingPayment ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="font-bold text-lg">{pkg.minutes} min</div>
                      <div className="text-sm text-muted-foreground">${pkg.price.toFixed(2)}</div>
                      {selectedPackage === pkg.minutes && (
                        <CheckCircle2 className="h-4 w-4 text-primary mx-auto mt-1" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowNoCreditsModal(false);
                  setSelectedPackage(null);
                }}
                disabled={isProcessingPayment}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={() => {
                  if (selectedPackage) {
                    purchaseMinutes(selectedPackage);
                  } else {
                    toast.error('Please select a package first');
                  }
                }}
                disabled={!selectedPackage || isProcessingPayment}
              >
                {isProcessingPayment ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Buy Minutes
                  </>
                )}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Welcome Modal */}
      <Dialog.Root open={showWelcomeModal} onOpenChange={(open) => {
        if (!open && userProfile?.id) {
          localStorage.setItem(`hasSeenWelcome:${userProfile.id}`, 'true');
        }
        setShowWelcomeModal(open);
      }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-primary/20 rounded-xl shadow-xl z-50 w-full max-w-lg p-8">
            <div className="text-center">
              <div className="mx-auto h-20 w-20 rounded-full bg-gradient-to-r from-primary/20 to-purple-500/20 flex items-center justify-center mb-6">
                <Disc3 className="h-10 w-10 text-primary" />
              </div>
              <Dialog.Title className="text-3xl font-bold mb-3 bg-gradient-to-r from-primary to-purple-400 bg-clip-text text-transparent">
                Welcome to RecordRinse.com!
              </Dialog.Title>
              <Dialog.Description className="text-muted-foreground text-lg mb-6">
                You've been credited with <span className="font-bold text-primary text-xl">5 free minutes</span> to explore all our features.
              </Dialog.Description>

              <div className="bg-secondary/30 rounded-lg p-6 mb-6 text-left">
                <h4 className="font-semibold mb-4 flex items-center gap-2 text-foreground">
                  <Info className="h-5 w-5 text-primary" />
                  What You Can Do With Your Free Minutes:
                </h4>
                <div className="space-y-3 text-sm text-muted-foreground">
                  <div className="flex items-start gap-3">
                    <Wand2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-foreground">Clean Explicit Content</span>
                      <p className="text-xs mt-0.5">Remove curse words from any song instantly</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Scissors className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-foreground">Separate Stems</span>
                      <p className="text-xs mt-0.5">Extract vocals, drums, bass, and more</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <BarChart3 className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-foreground">Track Analytics</span>
                      <p className="text-xs mt-0.5">Get BPM, key, chords, and song structure</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mic2 className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-foreground">AI Transcription</span>
                      <p className="text-xs mt-0.5">Convert audio to text with timestamps</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-primary/10 to-purple-500/10 border border-primary/20 rounded-lg p-4 mb-6">
                <p className="text-sm text-foreground">
                  <span className="font-semibold">How it works:</span> Minutes are used based on song duration. A 3-minute song uses 3 minutes, a 5-minute song uses 5 minutes.
                </p>
              </div>

              <Button
                size="lg"
                onClick={() => setShowWelcomeModal(false)}
                className="w-full bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90"
              >
                Get Started
                <Sparkles className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Delete Confirmation Modal */}
      <Dialog.Root open={!!deleteConfirmSession} onOpenChange={(open) => !open && setDeleteConfirmSession(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-xl shadow-xl z-50 w-full max-w-md p-6">
            <div className="text-center mb-6">
              <div className="mx-auto h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                <Trash2 className="h-8 w-8 text-destructive" />
              </div>
              <Dialog.Title className="text-xl font-bold mb-2">
                Delete Session Permanently?
              </Dialog.Title>
              <Dialog.Description className="text-muted-foreground">
                Are you sure you want to permanently delete <span className="font-semibold text-foreground">{deleteConfirmSession?.title || deleteConfirmSession?.filename}</span>?
                <br /><br />
                This action cannot be undone. All associated files will be permanently removed.
              </Dialog.Description>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setDeleteConfirmSession(null)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => deleteSession(deleteConfirmSession.id)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Permanently
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
